// Macro nutrient calculations
// Protein: 4 calories per gram
// Carbohydrates: 4 calories per gram
// Fats: 9 calories per gram

export interface MacroData {
    protein: number;
    carbs: number;
    fats: number;
    calories: number;
}

export interface MacroPercentages {
    protein: number;
    carbs: number;
    fats: number;
    total: number;
    isValid: boolean;
}

export interface MacroPreset {
    name: string;
    protein: number;
    carbs: number;
    fats: number;
    description: string;
}

export const MACRO_PRESETS: MacroPreset[] = [
    {
        name: 'Balanced',
        protein: 30,
        carbs: 40,
        fats: 30,
        description: 'Dieta balanceada padrão'
    },
    {
        name: 'Low Carb',
        protein: 30,
        carbs: 20,
        fats: 50,
        description: 'Baixo carboidrato, alta gordura'
    },
    {
        name: 'High Protein',
        protein: 40,
        carbs: 35,
        fats: 25,
        description: 'Alto proteína para ganho muscular'
    },
    {
        name: 'Ketogenic',
        protein: 25,
        carbs: 5,
        fats: 70,
        description: 'Dieta cetogênica'
    },
    {
        name: 'Mediterranean',
        protein: 20,
        carbs: 45,
        fats: 35,
        description: 'Estilo mediterrâneo'
    }
];

/**
 * Calculate macro percentages from grams and total calories
 */
export const calculateMacroPercentages = (
    proteinGrams: number,
    carbsGrams: number,
    fatsGrams: number,
    totalCalories: number
): MacroPercentages => {
    if (totalCalories <= 0) {
        return { protein: 0, carbs: 0, fats: 0, total: 0, isValid: false };
    }

    const proteinCals = proteinGrams * 4;
    const carbsCals = carbsGrams * 4;
    const fatsCals = fatsGrams * 9;
    const totalFromMacros = proteinCals + carbsCals + fatsCals;

    const proteinPercent = (proteinCals / totalCalories) * 100;
    const carbsPercent = (carbsCals / totalCalories) * 100;
    const fatsPercent = (fatsCals / totalCalories) * 100;
    const total = proteinPercent + carbsPercent + fatsPercent;

    // Valid if within 2% tolerance and macros sum to calories ±10%
    const isValid = Math.abs(total - 100) <= 2 && Math.abs(totalFromMacros - totalCalories) <= totalCalories * 0.1;

    return {
        protein: proteinPercent,
        carbs: carbsPercent,
        fats: fatsPercent,
        total,
        isValid
    };
};

/**
 * Calculate macro grams from percentages and total calories
 */
export const calculateMacroGrams = (
    proteinPercent: number,
    carbsPercent: number,
    fatsPercent: number,
    totalCalories: number
): MacroData => {
    const proteinCals = (totalCalories * proteinPercent) / 100;
    const carbsCals = (totalCalories * carbsPercent) / 100;
    const fatsCals = (totalCalories * fatsPercent) / 100;

    return {
        protein: Math.round(proteinCals / 4),
        carbs: Math.round(carbsCals / 4),
        fats: Math.round(fatsCals / 9),
        calories: totalCalories
    };
};

/**
 * Apply a macro preset to total calories
 */
export const applyMacroPreset = (preset: MacroPreset, totalCalories: number): MacroData => {
    return calculateMacroGrams(preset.protein, preset.carbs, preset.fats, totalCalories);
};

/**
 * Get color for macro percentage display (green if valid, yellow if close, red if off)
 */
export const getMacroPercentageColor = (percentages: MacroPercentages): string => {
    if (percentages.isValid) return '#10b981'; // green
    if (Math.abs(percentages.total - 100) <= 5) return '#f59e0b'; // yellow/amber
    return '#ef4444'; // red
};

/**
 * Format percentage for display
 */
export const formatPercentage = (value: number): string => {
    return `${value.toFixed(1)}%`;
};
